import { buildCompositionChartRows } from './compositionChartBuckets';
import {
	getCompositionChartFixedSliceOrder,
	isCompositionChartFixedSliceDimension,
	normalizeCompositionChartConfig,
	resolvePrimaryCompositionChartMetric,
} from './compositionChartConfig';
import { getPrimaryPhaseRowMetricValue } from '../../pipeline/lib/orderPrimaryPhaseMetric';
import { createEmptyPrimaryPhaseTotals } from '../../pipeline/lib/primaryPhaseOrderFinancials';
import { resolveCompareBaselineLabel, resolveTimelineAxisLabel } from '../../../filters/lib/statisticsCompareHelpers';

const toTime = (value) => {
	if (!value) return NaN;
	const time = new Date(value).getTime();
	return Number.isFinite(time) ? time : NaN;
};

/** 按系列日期窗过滤订单（下单日） */
export const filterOrdersByCreatedPeriod = (orders = [], startDate, endDate) => {
	const start = toTime(startDate);
	const end = toTime(endDate);
	if (!Number.isFinite(start) || !Number.isFinite(end)) return [];
	return (orders ?? []).filter((order) => {
		const created = toTime(order?.createdAt);
		if (!Number.isFinite(created)) return false;
		return created >= start && created <= end;
	});
};

/** 本期 + 顶栏对比期 → 系列列表 */
export const buildCompositionCompareSeriesList = ({
	currentPeriod = null,
	currentPeriodLabel = '',
	startDate = null,
	endDate = null,
	compares = [],
} = {}) => {
	const series = [];
	const currentStart = currentPeriod?.startDate || startDate;
	const currentEnd = currentPeriod?.endDate || endDate;
	if (currentStart && currentEnd) {
		series.push({
			key: 'current',
			label:
				currentPeriodLabel ||
				currentPeriod?.label ||
				resolveTimelineAxisLabel({
					kind: 'current',
					title: '本期',
					periodLabel: '',
					startDate: currentStart,
					endDate: currentEnd,
				}) ||
				'本期',
			startDate: currentStart,
			endDate: currentEnd,
		});
	}

	for (const compare of compares ?? []) {
		// 多层圈只用自选对比段。筛选栏自动同比/环比、以及与本期同一窗口的基准，都不进制作图。
		if (compare?.kind === 'yoy' || compare?.kind === 'mom' || compare?.kind === 'anchor') continue;
		if (!compare?.periodStartDate || !compare?.periodEndDate) continue;
		const key = String(compare.key || compare.kind || `${compare.periodStartDate}-${compare.periodEndDate}`);
		const sameWindow = series.some(
			(item) => item.startDate === compare.periodStartDate && item.endDate === compare.periodEndDate,
		);
		if (series.some((item) => item.key === key) || sameWindow) continue;
		series.push({
			key,
			label:
				resolveTimelineAxisLabel({
					kind: compare.kind,
					title: compare.title || resolveCompareBaselineLabel(compare),
					periodLabel: compare.periodLabel,
					startDate: compare.periodStartDate,
					endDate: compare.periodEndDate,
				}) ||
				resolveCompareBaselineLabel(compare) ||
				'对比期',
			startDate: compare.periodStartDate,
			endDate: compare.periodEndDate,
		});
	}

	return series;
};

const createEmptyAlignedRow = (key, label) => ({
	...createEmptyPrimaryPhaseTotals(),
	key,
	label,
});

/**
 * 对比系列 × 维度矩阵：扇区键并集对齐，缺键补 0。
 * @returns {{ metric, series: Array<{ key, label, items, totalMetric }> } | null}
 */
export const buildCompositionCompareSeriesMatrix = ({
	orders = [],
	seriesList = [],
	chartConfig,
	selectedPhase = null,
	filters = {},
} = {}) => {
	if (!Array.isArray(seriesList) || seriesList.length < 2) return null;

	const config = normalizeCompositionChartConfig(chartConfig);
	const metric = resolvePrimaryCompositionChartMetric(config.metrics);
	const dimension = config.dimension;

	const seriesBuckets = seriesList.map((series) => {
		const periodOrders = filterOrdersByCreatedPeriod(orders, series.startDate, series.endDate);
		const { items, totalCount } = buildCompositionChartRows({
			orders: periodOrders,
			dimension,
			metrics: [metric],
			selectedPhase,
			filters,
		});
		return { ...series, items, totalCount };
	});

	const labelByKey = new Map();
	const keySet = new Set();

	if (isCompositionChartFixedSliceDimension(dimension)) {
		for (const slot of getCompositionChartFixedSliceOrder(dimension)) {
			keySet.add(slot.key);
			labelByKey.set(slot.key, slot.label);
		}
	} else {
		for (const series of seriesBuckets) {
			for (const row of series.items) {
				keySet.add(row.key);
				if (!labelByKey.has(row.key)) labelByKey.set(row.key, row.label);
			}
		}
	}

	let alignedKeys = [...keySet];
	if (!isCompositionChartFixedSliceDimension(dimension)) {
		alignedKeys.sort((a, b) => {
			const maxA = Math.max(...seriesBuckets.map((series) => getPrimaryPhaseRowMetricValue(series.items.find((row) => row.key === a), metric)));
			const maxB = Math.max(...seriesBuckets.map((series) => getPrimaryPhaseRowMetricValue(series.items.find((row) => row.key === b), metric)));
			return maxB - maxA || String(labelByKey.get(a) ?? a).localeCompare(String(labelByKey.get(b) ?? b), 'zh-CN');
		});
	}

	const series = seriesBuckets.map((bucket) => {
		const byKey = new Map(bucket.items.map((row) => [row.key, row]));
		const items = alignedKeys.map((key) => byKey.get(key) ?? createEmptyAlignedRow(key, labelByKey.get(key) ?? key));
		const totalMetric = items.reduce((sum, row) => sum + getPrimaryPhaseRowMetricValue(row, metric), 0);
		return {
			key: bucket.key,
			label: bucket.label,
			items,
			totalMetric,
			totalCount: bucket.totalCount ?? 0,
		};
	});

	return { metric, dimension, series };
};
